import DOMPurify from 'dompurify';
import { uniq } from 'lodash';

interface ISanitizer {
  sanitize: (markdown: string) => string;
}

export interface SanitizerOptions {
  // Replace DOMPurify's default allow-lists. Attributes are allowed per tag, as the server's are:
  // DOMPurify's own attribute list is global, so the per-tag check runs in a hook.
  allowedTags?: readonly string[];
  allowedAttributes?: Readonly<Record<string, readonly string[]>>;
  // URL schemes an `href`/`src` may use. Anything else, relative URLs included, is dropped.
  allowedUriProtocols?: readonly string[];
  // CSS properties a `style` attribute may keep; every other declaration is dropped. DOMPurify
  // itself never looks inside `style`, so without this any allowed `style` passes through whole.
  allowedStyles?: readonly string[];
  // Of `allowedStyles`, the ones only an <img> may keep (a size, so no other element can restyle the page).
  imageOnlyStyles?: readonly string[];
  // When set, an <img> keeps its `src` only as embedded base64 data of one of these MIME types.
  allowedEmbeddedImageTypes?: readonly string[];
  // Force every link to open in a new tab (with rel="noopener noreferrer").
  openLinksInNewTab?: boolean;
}

// DOMPurify checks `allowedUriProtocols` against every attribute it does not know to be URL-free, so
// a `width="624"` would fail it; only these carry a URL.
const URL_ATTRIBUTES = ['href', 'src'];

const filterStyle = (style: string, allowedStyles: readonly string[]) =>
  style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const property = declaration.split(':')[0]?.trim().toLowerCase();
      return property && allowedStyles.includes(property) && !/url\(|expression\(/i.test(declaration);
    })
    .join('; ');

// this hook is used to sanitize markdown text before rendering it
export const useSanitizer = (options: SanitizerOptions = {}): ISanitizer => {
  const {
    allowedTags,
    allowedAttributes,
    allowedUriProtocols,
    allowedStyles,
    imageOnlyStyles = [],
    allowedEmbeddedImageTypes,
    openLinksInNewTab = false,
  } = options;
  const attributeNames = allowedAttributes && uniq(Object.values(allowedAttributes).flat());
  const embeddedImageSrc = allowedEmbeddedImageTypes
    ? new RegExp(`^data:(?:${allowedEmbeddedImageTypes.join('|')});base64,`, 'i')
    : null;

  const sanitize = (markdown: string) => {
    // to reduce the security risk of allowing the target="_blank", creating a named hook function for adding rel attributes with noopener noreferrer.
    const addRelAttributeHook = (node: Element) => {
      if (node.nodeName === 'A' && openLinksInNewTab) {
        node.setAttribute('target', '_blank');
      }
      if (node.nodeName === 'A' && node.hasAttribute('target') && node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
      // An image whose source was rejected would only render as a broken-image box.
      if (embeddedImageSrc && node.nodeName === 'IMG' && !node.hasAttribute('src')) {
        node.remove();
      }
    };

    const filterAttributeHook = (node: Element, data: DOMPurify.UponSanitizeAttributeHookEvent) => {
      const tag = node.nodeName.toLowerCase();
      if (allowedAttributes && !allowedAttributes[tag]?.includes(data.attrName)) {
        data.keepAttr = false;
        return;
      }
      if (allowedStyles && data.attrName === 'style') {
        const styles =
          tag === 'img' ? allowedStyles : allowedStyles.filter((style) => !imageOnlyStyles.includes(style));
        data.attrValue = filterStyle(data.attrValue, styles);
        data.keepAttr = !!data.attrValue;
      }
      if (embeddedImageSrc && node.nodeName === 'IMG' && data.attrName === 'src') {
        data.keepAttr = embeddedImageSrc.test(data.attrValue);
      }
    };

    // Add the hook with a reference
    DOMPurify.addHook('afterSanitizeAttributes', addRelAttributeHook);
    DOMPurify.addHook('uponSanitizeAttribute', filterAttributeHook);

    try {
      return DOMPurify.sanitize(markdown, {
        FORBID_ATTR: ['rel'],
        ADD_ATTR: ['target'],
        ...(allowedTags && { ALLOWED_TAGS: [...allowedTags] }),
        ...(attributeNames && { ALLOWED_ATTR: attributeNames }),
        ...(allowedUriProtocols && {
          ALLOWED_URI_REGEXP: new RegExp(`^(?:${allowedUriProtocols.join('|')}):`, 'i'),
          ...(attributeNames && { ADD_URI_SAFE_ATTR: attributeNames.filter((name) => !URL_ATTRIBUTES.includes(name)) }),
        }),
      });
    } finally {
      // Remove the specific hooks when done
      DOMPurify.removeHook('afterSanitizeAttributes');
      DOMPurify.removeHook('uponSanitizeAttribute');
    }
  };

  return {
    sanitize,
  };
};
